<script setup lang="ts">
import { Dialog as HDialog, DialogPanel as HDialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { twMerge } from "tailwind-merge";

const emit = defineEmits(["update:modelValue", "close"]);
const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    appear?: boolean;
    overlay?: boolean;
    transition?: boolean;
    preventClose?: boolean;
    dialogClass?: string;
    fullscreen?: boolean;
    position?: "center" | "top" | "bottom" | "bottom-left" | "bottom-right" | "top-left" | "top-right";
  }>(),
  {
    modelValue: false,
    appear: false,
    overlay: true,
    transition: true,
    preventClose: false,
    fullscreen: false,
    position: "center",
  },
);
const isOpen = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const transitionClass = computed(() => {
  if (!props.transition)
    return {};

  switch (props.position) {
    case "bottom-right":
    case "top-right":
      return {
        enter: "transition ease-out duration-300",
        enterFrom: "translate-x-full",
        enterTo: "translate-x-0",
        leave: "transition ease-in duration-200",
        leaveFrom: "translate-x-0",
        leaveTo: "translate-x-full",
      };
    case "bottom-left":
    case "top-left":
      return {
        enter: "transition ease-out duration-300",
        enterFrom: "-translate-x-full",
        enterTo: "translate-x-0",
        leave: "transition ease-in duration-200",
        leaveFrom: "translate-x-0",
        leaveTo: "-translate-x-full",
      };
    case "bottom":
      return {
        enter: "transition ease-out duration-300",
        enterFrom: "translate-y-full",
        enterTo: "translate-y-0",
        leave: "transition ease-in duration-200",
        leaveFrom: "translate-y-0",
        leaveTo: "translate-y-full",
      };
    case "top":
      return {
        enter: "transition ease-out duration-300",
        enterFrom: "-translate-y-full",
        enterTo: "translate-y-0",
        leave: "transition ease-in duration-200",
        leaveFrom: "translate-y-0",
        leaveTo: "-translate-y-full",
      };

    default:
      return {
        enter: "transition ease-out duration-300",
        enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
        enterTo: "opacity-100 translate-y-0 sm:scale-100",
        leave: "transition ease-in duration-200",
        leaveFrom: "opacity-100 translate-y-0 sm:scale-100",
        leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
      };
  }
});

const dialogPanelClass = computed(() =>
  twMerge(
    "relative text-left rtl:text-right overflow-hidden w-full flex flex-col",
    "bg-white dark:bg-neutral-dark-800",
    "shadow-xl",
    props.dialogClass,
    props.fullscreen ? "w-screen" : "sm:max-w-lg",
    props.fullscreen ? "h-screen" : "",
    props.fullscreen ? "rounded-none" : "rounded-lg",
    props.fullscreen ? "m-0" : "sm:my-8",
  ),
);

const overlayTransition = {
  enter: "transition ease-out duration-300",
  enterFrom: "opacity-0",
  enterTo: "opacity-100",
  leave: "transition ease-in duration-200",
  leaveFrom: "opacity-100",
  leaveTo: "opacity-0",
};

const positionClass = computed(() => {
  if (props.fullscreen) {
    return "flex items-center justify-center";
  }
  switch (props.position) {
    case "top":
      return "flex min-h-full items-start justify-center py-20";
    case "bottom":
      return "flex min-h-full items-end justify-center";
    case "bottom-left":
      return "flex min-h-full items-end justify-start px-10";
    case "bottom-right":
      return "flex min-h-full items-end justify-end px-10";
    case "top-left":
      return "flex min-h-full items-start justify-start py-20 px-10";
    case "top-right":
      return "flex min-h-full items-start justify-end py-20 px-10";
    case "center":
    default:
      return "flex min-h-full items-center justify-center p-4 sm:p-0";
  }
});

function close(value: boolean) {
  isOpen.value = value;
  emit("close");
}
</script>

<template>
  <TransitionRoot :appear="props.appear" :show="isOpen" as="template">
    <HDialog class="relative z-[110]" @close="(e) => !props.preventClose && close(e)">
      <TransitionChild v-if="props.overlay" as="template" :appear="props.appear" v-bind="overlayTransition">
        <div class="fixed inset-0 transition-opacity bg-[#000] dark:bg-[#000] opacity-65" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div :class="positionClass">
          <TransitionChild as="template" :appear="props.appear" v-bind="transitionClass">
            <HDialogPanel :class="dialogPanelClass">
              <slot />
            </HDialogPanel>
          </TransitionChild>
        </div>
      </div>
    </HDialog>
  </TransitionRoot>
</template>
